/**
 * The device credential (PLAN.md §3, F5) — the THIRD token shape the
 * API layer can present, beside the user JWT (`authStore.token`) and the
 * shipped panel token (`touch_device_token`).
 *
 * A device that a hub itself admits holds a credential that hub signed:
 * `{ sub, role, dev, typ: "device" }` with a 90-day life, rotated on use
 * through `POST /auth/device/refresh`. Two properties decide how it may be
 * used, and both are why this is not "just another token":
 *
 *  1. **It is hub-scoped by construction.** The signature is that hub's
 *     `JWT_SECRET`, so the credential is stored per *connection* (the same
 *     `local:host:port/api` / `hub:<hubId>` keys the rest of the per-hub state
 *     uses) and is never offered to another hub. A single shared key would
 *     hand hub A's credential to hub B, which answers 401 with a credential
 *     that was perfectly good — the exact class of damage the stale
 *     `touch_device_token` did to desktop sessions.
 *  2. **It is accepted on a defined subset of the hub's routes** — the routers
 *     the hub mounts with `authenticateAny` instead of `authenticate`
 *     (`otto/src/server/routes/*`: `/devices`, `/rooms`, `/areas`,
 *     `/scenes`, `/cameras`, `/energy`, `/security`, `/media-players`,
 *     `/media-library`, `/audio-zones`, `/nodes`, `/integrations`, images'
 *     `…/content`, `/system/status*`, `/system/platform*`, and the
 *     credential's own `/auth/device/refresh` + `/auth/devices`). Presenting
 *     it anywhere else is a 401 the client asked for.
 *
 * So this module owns the *policy* — which endpoints may carry it, when it is
 * due for rotation, what a rejection looks like — and the *storage*. It owns no
 * session state and no UI: `stores/auth.ts` is the session facade (it keeps the
 * reactive copy, calls the refresh, and owns the user-visible consequence) and
 * `services/api.ts` is the only place that decides which token goes out.
 *
 * ── Who writes it (card `t_6cbbce4c`) ───────────────────────────────────────
 *
 * ── Who writes it (card `t_6cbbce4c`, `t_2f0c4a91`) ────────────────────────
 *
 * A **panel**: it has no Torik account and cannot claim, so pairing — and the
 * credential this hub mints for it — is its way into a hub. A user's own device
 * keeps the hub session the claim mints instead (`hubAccessToken:<host>:<port>`),
 * and holds no `typ:"device"` credential at all (plan
 * `docs/plans/LOCAL-FIRST-CONNECT.md` §2/D4). The writers left are panel pairing,
 * the hub's own adoption route (`POST /api/hub/adopt`, which binds the claiming
 * device) and the rotation inside `stores/auth.ts` for a credential already held;
 * the code chain — `/enroll`, `/auth/enroll/*`, the bootstrap wizard — is gone
 * (card `t_2f0c4a91`). This module's
 * *reader* is asked by `services/hubBinding.ts` → `hasStoredDeviceCredential`,
 * which is the panel fact of the discovery table.
 */
import { getHubOwnKeys, getHubSessionKeys } from '../config/runtime';

/** The key the enrolment flow used before credentials were hub-scoped. */
export const DEVICE_CREDENTIAL_STORAGE_KEY = 'torik_device_token';

/** Per-connection variant: `torik_device_token:hub:<id>` / `…:local:host:port/api`. */
export const DEVICE_CREDENTIAL_KEY_PREFIX = `${DEVICE_CREDENTIAL_STORAGE_KEY}:`;

/**
 * Rotate this far ahead of the expiry. The credential lives 90 days and the hub
 * rotates it on use, so a device that is opened at least once a fortnight never
 * meets an expiry it cannot come back from.
 */
export const DEVICE_CREDENTIAL_REFRESH_SKEW_MS = 7 * 24 * 60 * 60 * 1000;

/** What the hub signs into a device credential (F5). */
export interface DeviceCredentialClaims {
  /** Hub user the device acts for. */
  sub: string;
  role: string;
  /** `device_bindings.device_key`. */
  dev: string;
  typ: 'device';
  /** Expiry, seconds since epoch (standard JWT claim). */
  exp?: number;
  iat?: number;
  jti?: string;
}

export interface StoredDeviceCredential {
  token: string;
  /** Where it was read from — the legacy key means "migrate me". */
  storageKey: string;
  claims: DeviceCredentialClaims;
  /** `exp` as ms, or null when the credential carries no readable expiry. */
  expiresAtMs: number | null;
}

export interface DeviceCredentialStorageOptions {
  storage?: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> | null;
  /** Connection keys, most specific first. Defaults to the live connection. */
  keys?: string[];
}

// ─── Reading the credential itself ───────────────────────────────────────────

/**
 * Decode a device credential's payload. Pure and signature-free on purpose:
 * whether the token is *good* is the hub's call (`/auth/device/refresh` is the
 * one route that answers that question with `code: "invalid_device_token"`), and
 * this side only needs to know which claims to display and when it expires.
 *
 * Returns null for anything that is not a `typ:"device"` credential, so a user
 * JWT that ended up under this key can never be mistaken for one.
 */
export function decodeDeviceCredential(token: string | null | undefined): DeviceCredentialClaims | null {
  if (!token || typeof token !== 'string') return null;
  const parts = token.split('.');
  if (parts.length !== 3) return null;
  try {
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
    const json = typeof atob === 'function' ? atob(padded) : Buffer.from(padded, 'base64').toString('binary');
    const payload = JSON.parse(decodeURIComponent(escape(json)));
    if (!payload || typeof payload !== 'object') return null;
    if (payload.typ !== 'device') return null;
    if (typeof payload.sub !== 'string' || typeof payload.dev !== 'string') return null;
    return {
      sub: payload.sub,
      role: typeof payload.role === 'string' ? payload.role : 'resident',
      dev: payload.dev,
      typ: 'device',
      exp: typeof payload.exp === 'number' ? payload.exp : undefined,
      iat: typeof payload.iat === 'number' ? payload.iat : undefined,
      jti: typeof payload.jti === 'string' ? payload.jti : undefined,
    };
  } catch {
    return null;
  }
}

/** Expiry in ms, or null when the credential carries none. */
export function deviceCredentialExpiresAtMs(claims: DeviceCredentialClaims | null): number | null {
  return typeof claims?.exp === 'number' ? claims.exp * 1000 : null;
}

// ─── Storage ─────────────────────────────────────────────────────────────────

/**
 * Does this connection key name a HOST only, instead of a hub?
 *
 * The pre-port spelling `local:<host>/api` is host-scoped: two hubs on one host
 * share it, so a credential stored there answers for whichever of them asks
 * next. That is how a genuinely fresh hub reads as one this device already
 * holds — `services/hubBinding.ts` → `hasStoredDeviceCredential` finds hub A's
 * credential under the shared key, and the Local card offers "Open" on a hub the
 * app has nothing for instead of "Adopt" (measured on card `t_947d6b10` §6.3,
 * where it cost one fixture reset in the adoption proof).
 *
 * The rule for that legacy shape, in one place:
 *
 *  1. **a write never creates one.** `storeDeviceCredential` (and
 *     `clearDeviceCredential`) keep the hub-scoped keys — `hub:<id>` and
 *     `local:<host>:<port>/api` — so an adoption, a rotation or a migration
 *     cannot leave a host-scoped copy behind for the next hub on that host to
 *     read. Every write is a hub-scoped fact, not just an adoption: a rotation
 *     writes the same credential for the same hub;
 *  2. **it stays readable**: `readDeviceCredential` still finds a credential a
 *     build without port-scoped keys wrote, and the session layer migrates it by
 *     re-storing it under (1)'s keys;
 *  3. **it is not evidence for a hub that can name itself**:
 *     `deviceCredentialKeys` consults it only when the hub's own id is unknown,
 *     because a hub that answers `/api/hub/identity` has keys of its own to be
 *     found under, and a key that cannot say which hub it belongs to must not
 *     answer for it.
 */
export function isHostScopedCredentialKey(key: string): boolean {
  if (!key.startsWith('local:')) return false;
  const endpoint = key.slice('local:'.length).replace(/\/api$/, '');
  if (endpoint.startsWith('[')) {
    // `[fe80::1]` is a host; `[fe80::1]:16161` is a hub address.
    const closing = endpoint.indexOf(']');
    return closing === -1 || closing === endpoint.length - 1;
  }
  return !/:\d+$/.test(endpoint);
}

function resolveStorage(
  options: DeviceCredentialStorageOptions,
): Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> | null {
  if (options.storage !== undefined) return options.storage;
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

/** The connection's own keys (hub id when known, else this endpoint). */
function ownKeys(): string[] {
  try {
    return getHubOwnKeys();
  } catch {
    return [];
  }
}

/**
 * Every key that identifies the connection the app is on, hub id first, then
 * this endpoint. Used for WRITES so a credential enrolled over the LAN is still
 * found by a cloud (`hub:<id>`) session to the same hub — and for reads only
 * after the strict own-keys pass, which is what keeps the order safe.
 */
function connectionKeys(): string[] {
  const keys: string[] = [];
  try {
    for (const key of getHubSessionKeys()) if (!keys.includes(key)) keys.push(key);
  } catch {
    // continue with the strict keys
  }
  for (const key of ownKeys()) if (!keys.includes(key)) keys.push(key);
  return keys;
}

/**
 * Read order: this connection's own keys first (the strict list — never
 * `getHubSessionKeys()`'s fallback, which can hand back the hub being *left*),
 * then the wider connection list, then the legacy single key.
 */
function resolveReadKeys(options: DeviceCredentialStorageOptions): string[] {
  if (options.keys) return options.keys;
  const keys = ownKeys();
  for (const key of connectionKeys()) if (!keys.includes(key)) keys.push(key);
  return keys;
}

/**
 * Write/clear reach: every key of this connection, plus the legacy key — minus the
 * **host-scoped** spellings, which no write may create (see
 * `isHostScopedCredentialKey`).
 */
function resolveConnectionKeys(options: DeviceCredentialStorageOptions): string[] {
  return (options.keys ?? connectionKeys()).filter((key) => !isHostScopedCredentialKey(key));
}

function prefixed(keys: string[]): string[] {
  return keys.map((key) => `${DEVICE_CREDENTIAL_KEY_PREFIX}${key}`);
}

/**
 * The credential for the connection the app is on right now.
 *
 * Reads this connection's own keys first (never the `getHubSessionKeys()`
 * fallback chain — that one hands back the hub being *left*), then the legacy
 * single key written before credentials were scoped, which the session layer
 * migrates by re-storing it.
 */
export function readDeviceCredential(options: DeviceCredentialStorageOptions = {}): StoredDeviceCredential | null {
  const storage = resolveStorage(options);
  if (!storage) return null;

  for (const key of prefixed(resolveReadKeys(options))) {
    const value = safeGet(storage, key);
    const credential = value ? toCredential(value, key) : null;
    if (credential) return credential;
  }

  const legacy = safeGet(storage, DEVICE_CREDENTIAL_STORAGE_KEY);
  if (legacy) {
    const credential = toCredential(legacy, DEVICE_CREDENTIAL_STORAGE_KEY);
    if (credential) return credential;
  }

  return null;
}

function toCredential(token: string, storageKey: string): StoredDeviceCredential | null {
  const claims = decodeDeviceCredential(token);
  if (!claims) return null;
  return { token, storageKey, claims, expiresAtMs: deviceCredentialExpiresAtMs(claims) };
}

/**
 * Store a (freshly redeemed or rotated) credential for this connection, under
 * every **hub-scoped** key of the connection (as the per-hub view state does),
 * and drop the legacy single key so it cannot outlive this write and be offered
 * to another hub.
 *
 * The host-only spelling `local:<host>/api` is deliberately NOT written — see
 * `isHostScopedCredentialKey`: a key that names a host and not a hub would be
 * read back by the next hub on that host as a binding this device does not hold.
 */
export function storeDeviceCredential(
  token: string,
  options: DeviceCredentialStorageOptions = {},
): StoredDeviceCredential | null {
  const storage = resolveStorage(options);
  const claims = decodeDeviceCredential(token);
  if (!storage || !claims) return null;

  const keys = prefixed(resolveConnectionKeys(options));
  for (const key of keys) safeSet(storage, key, token);
  safeRemove(storage, DEVICE_CREDENTIAL_STORAGE_KEY);

  return {
    token,
    storageKey: keys[0] ?? DEVICE_CREDENTIAL_STORAGE_KEY,
    claims,
    expiresAtMs: deviceCredentialExpiresAtMs(claims),
  };
}

/**
 * Forget the credential of this connection (and the legacy copy).
 *
 * Host-scoped keys are left alone: they are not this connection's key to clear
 * (`isHostScopedCredentialKey`) — nothing may be *written* under them any more,
 * but a copy an older build left there belongs to no single hub, and deleting it
 * would take away the one legacy fallback the read path still honours.
 */
export function clearDeviceCredential(options: DeviceCredentialStorageOptions = {}): void {
  const storage = resolveStorage(options);
  if (!storage) return;
  for (const key of prefixed(resolveConnectionKeys(options))) safeRemove(storage, key);
  safeRemove(storage, DEVICE_CREDENTIAL_STORAGE_KEY);
}

/**
 * Forget every **host-scoped** credential a build before the port-scoped keys
 * left behind (`torik_device_token:local:<host>/api`).
 *
 * This is the one spelling no per-connection clear reaches: it names a host, not
 * a hub (`isHostScopedCredentialKey`), so `clearDeviceCredential` above leaves it
 * alone on purpose — it is the legacy fallback a hub that cannot name itself is
 * still allowed to read.
 *
 * It is called when a hub session lands from a claim
 * (`stores/auth.ts` → `adoptClaimedIdentity`): the credential is retired for that
 * class (a user's own device — card `t_6cbbce4c`), and the whole point of the
 * exercise is that the shape which caused the measured defect — the only
 * credential on the dev machine was filed under the host-only loopback key the
 * lookup skips — stops existing. A panel's credential never lives here: no write
 * creates a host-scoped key, so this cannot take a panel's door away, and a panel
 * window has no account and never claims.
 *
 * Answers the keys it removed, for the log line (never their contents).
 */
export function clearHostScopedDeviceCredentials(options: { storage?: Storage | null } = {}): string[] {
  let storage: Storage | null = null;
  if (options.storage !== undefined) {
    storage = options.storage;
  } else {
    try {
      storage = typeof localStorage === 'undefined' ? null : localStorage;
    } catch {
      storage = null;
    }
  }
  if (!storage) return [];

  const doomed: string[] = [];
  try {
    for (let index = 0; index < storage.length; index += 1) {
      const key = storage.key(index);
      if (!key || !key.startsWith(DEVICE_CREDENTIAL_KEY_PREFIX)) continue;
      if (!isHostScopedCredentialKey(key.slice(DEVICE_CREDENTIAL_KEY_PREFIX.length))) continue;
      doomed.push(key);
    }
  } catch {
    return [];
  }
  for (const key of doomed) safeRemove(storage, key);
  return doomed;
}

function safeGet(storage: Pick<Storage, 'getItem'>, key: string): string | null {
  try {
    const value = storage.getItem(key);
    return value && value.length > 0 ? value : null;
  } catch {
    return null;
  }
}

function safeSet(storage: Pick<Storage, 'setItem'>, key: string, value: string): void {
  try {
    storage.setItem(key, value);
  } catch {
    // Storage full / unavailable: the credential still lives in memory for
    // this session.
  }
}

function safeRemove(storage: Pick<Storage, 'removeItem'>, key: string): void {
  try {
    storage.removeItem(key);
  } catch {
    // ignore
  }
}

// ─── Where it may be offered ─────────────────────────────────────────────────

/**
 * Hub route families mounted with `authenticateAny`, i.e. the ones that accept
 * a device credential. Grounded in the hub's own routers — when a family below
 * moves to `authenticate`, the credential stops being accepted there and this
 * list is what has to follow it.
 */
export const DEVICE_REACHABLE_PREFIXES = [
  '/auth/device/', // the credential's own lifecycle: refresh
  '/auth/devices', // its own binding: revoke self
  '/devices',
  '/rooms',
  '/areas',
  '/scenes',
  '/cameras',
  '/energy/',
  '/security/',
  '/media-players',
  '/media-library',
  '/audio-zones',
  '/nodes',
  '/integrations',
  '/images/',
  '/system/status',
  '/system/platform',
] as const;

/**
 * May this request carry the enrolled device credential?
 *
 * Deliberately a list and not "anything that is not a user route": the route a
 * stale credential is offered on is exactly the route that will not accept it,
 * and a device credential presented to `/users/me` or `/auth/login` is a 401
 * with an explanation only the hub can give. Absolute URLs are normalised
 * (origin and the `/api` prefix dropped) because callers pass both shapes.
 */
export function isDeviceReachableEndpoint(endpoint: string): boolean {
  const path = normalizeEndpointPath(endpoint);
  if (!path) return false;
  return DEVICE_REACHABLE_PREFIXES.some(
    (prefix) =>
      path === prefix ||
      // A family prefix matches only on a path boundary, so `/devices-x` is not
      // `/devices` and a typo cannot turn into a credential leak.
      path.startsWith(prefix.endsWith('/') ? prefix : `${prefix}/`),
  );
}

/**
 * `/api/devices?x=1`, `http://host:16161/api/devices` and `/devices` → `/devices`.
 *
 * Exported because the credential precedence rule (`services/apiCredential.ts`)
 * has to classify the same endpoints this module decides reachability for, and
 * two normalisers would drift.
 */
export function normalizeEndpointPath(endpoint: string): string | null {
  if (!endpoint || typeof endpoint !== 'string') return null;
  let path = endpoint.trim();
  if (!path) return null;

  if (/^[a-z][a-z\d+.-]*:\/\//i.test(path)) {
    try {
      path = new URL(path).pathname;
    } catch {
      return null;
    }
  }

  path = path.split('?')[0].split('#')[0];
  if (!path.startsWith('/')) path = `/${path}`;
  if (path === '/api' || path.startsWith('/api/')) path = path.slice(4);
  return path || null;
}

// ─── Rotation policy ─────────────────────────────────────────────────────────

/** Is this credential close enough to expiring that it should rotate first? */
export function shouldRefreshDeviceCredential(
  credential: StoredDeviceCredential | null,
  now: number = Date.now(),
  skewMs: number = DEVICE_CREDENTIAL_REFRESH_SKEW_MS,
): boolean {
  if (!credential) return false;
  // No readable expiry: rotate rather than wait for a 401 to find out.
  if (credential.expiresAtMs === null) return true;
  return credential.expiresAtMs - now <= skewMs;
}

/**
 * Is this the hub saying "that credential is not valid any more"?
 *
 * `invalid_device_token` is the refresh route's own code and the only
 * unambiguous one; every other 401 can equally be a route that wants a user
 * JWT, which is why the caller asks the refresh route before believing it.
 */
export function isDeviceCredentialRejected(error: unknown): boolean {
  const apiError = error as { status?: number; data?: { code?: string } } | null;
  if (apiError?.status !== 401) return false;
  return apiError.data?.code === 'invalid_device_token';
}
